import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  makeApi,
  type Issue,
  type IssuePriority,
  type IssueStatus,
  type Me,
} from '../api/client';
import { config } from '../config';
import { IssuesTable } from '../components/IssuesTable';

export function IssuesPage(): JSX.Element {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [me, setMe] = useState<Me | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Filters (client-side — customer list is bounded by their own issues).
  const [projectId, setProjectId] = useState('');
  const [status, setStatus] = useState<'' | IssueStatus>('');
  const [priority, setPriority] = useState<'' | IssuePriority>('');

  useEffect(() => {
    (async () => {
      try {
        const m = await api.me();
        setMe(m);
        if (m.accountStatus === 'approved') {
          const { issues: items } = await api.listMyIssues();
          setIssues(items);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  // Projects the customer is linked to, for the Project filter dropdown.
  const linkedProjects = useMemo(() => {
    if (!me) return [] as { projectId: string; label: string }[];
    const byId = new Map<string, { projectId: string; label: string }>();
    for (const a of me.addresses) {
      for (const p of a.projects) {
        if (byId.has(p.projectId)) continue;
        byId.set(p.projectId, {
          projectId: p.projectId,
          label: p.displayName
            ? `${p.displayName} (${p.projectReferenceNumber})`
            : p.projectReferenceNumber,
        });
      }
    }
    return [...byId.values()];
  }, [me]);

  const filtered = useMemo(() => {
    return issues.filter((i) => {
      if (projectId && i.projectId !== projectId) return false;
      if (status && i.status !== status) return false;
      if (priority && i.priority !== priority) return false;
      return true;
    });
  }, [issues, projectId, status, priority]);

  if (loading) return <div className="text-gray-500">Loading…</div>;
  if (error) return <div className="text-red-400">{error}</div>;
  if (!me) return <div className="text-red-400">No profile</div>;

  const hasRed =
    me.flagReasons.includes('duplicate_address_claim') ||
    me.flagReasons.includes('invalid_address_id');
  const hasAmber = me.flagReasons.includes('no_project_mapping');

  return (
    <div className="space-y-4">
      {hasRed && (
        <div className="alert-red">
          We need to verify your account before you can file issues. Please contact support at{' '}
          <a className="underline hover:text-red-200" href={`mailto:${config.supportEmail}`}>
            {config.supportEmail}
          </a>
          .
        </div>
      )}
      {!hasRed && hasAmber && (
        <div className="alert-amber">
          Admin is linking your address to a project. You&rsquo;ll be able to file issues shortly.
        </div>
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-white">My issues</h1>
        {me.accountStatus === 'approved' && (
          <Link to="/issues/new" className="btn-primary text-sm">
            + New issue
          </Link>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-end">
        {linkedProjects.length > 1 && (
          <div>
            <label className="label">Project</label>
            <select
              className="input"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="">All projects</option>
              {linkedProjects.map((p) => (
                <option key={p.projectId} value={p.projectId}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="label">Status</label>
          <select
            className="input"
            value={status}
            onChange={(e) => setStatus(e.target.value as IssueStatus | '')}
          >
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
        <div>
          <label className="label">Priority</label>
          <select
            className="input"
            value={priority}
            onChange={(e) => setPriority(e.target.value as IssuePriority | '')}
          >
            <option value="">All</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
      </div>

      <IssuesTable
        issues={filtered}
        buildIssueHref={(i) => `/issues/${i.issueId}`}
      />
    </div>
  );
}
